import { IApplicationState } from '@nkr/store/index';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { RouteComponentProps } from 'react-router';
import type { Dispatch } from 'redux';

function parseQuery(search: string): URLSearchParams {
    return new URLSearchParams(search || '');
}

// Context for exposing Redux app slice and router props via a hook
export type AppHookContextValue = IApplicationState & {
    dispatch: Dispatch<any>;
    centerId: number | null;
    isReady: boolean;
    location: { pathname: string; search: string; hash?: string };
    match: RouteComponentProps<any>['match'];
    params: Record<string, string | undefined>;
    query: URLSearchParams;
};

export const AppHookContext = React.createContext<AppHookContextValue | null>(null);

type ProviderOwnProps = { children?: React.ReactNode };
type ProviderStateProps = { app: any };
type ProviderDispatchProps = { dispatch: Dispatch<any> };
type ProviderRouterProps = RouteComponentProps<any>;
type ProviderProps = ProviderOwnProps & ProviderStateProps & ProviderDispatchProps & ProviderRouterProps;

const AppHookProviderBase = (props: ProviderProps) => {
    const { app, dispatch, location, match, children } = props;
    const params = (match?.params ?? {}) as Record<string, string | undefined>;

    const centerId = (app?.app && (app.app?.user?.centerId || app.app?.center?.id)) || null;

    const value = React.useMemo(() => ({
        dispatch,
        // app,
        // user: app?.user ?? null,
        ...app,
        isReady: !!app?.app?.user,
        location: { pathname: location.pathname, search: location.search, hash: (location as any).hash },
        match,
        params,
        query: parseQuery(location.search),
        centerId,
    }), [dispatch, app, location.pathname, location.search, (location as any).hash, JSON.stringify(params), centerId]);

    return React.createElement(AppHookContext.Provider, { value }, children as any);
};

const mapState = (state: IApplicationState): ProviderStateProps => ({ app: state });

// Wrap with withRouter first to inject router props, then connect for Redux state/dispatch
const RoutedAppHookProviderBase = withRouter(AppHookProviderBase as any) as any;
export const AppHookProvider = connect(mapState)(RoutedAppHookProviderBase) as any;

export function useAppHookContext() {
    const ctx = React.useContext(AppHookContext);
    if (!ctx) throw new Error('useAppHook must be used within <AppHookProvider>');
    return ctx;
}